import React, { useState } from "react";
import {
  Box,
  Center,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../components/UI/CustomInput";
import CustomButton from "../../components/UI/CustomButton";
import { validateEmail } from "../../helpers/utils";
import AuthLogo from "../../components/UI/AuthLogo";
import { useAuth } from "../../context/AuthContext";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";
import { getLocalizedAuthErrorMessage } from "../../helpers/authErrors";
import { useFormTheme } from "../../hooks/useFormTheme";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const authCardTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const { sendResetCodeHandler, isLoading } = useAuth();
  const {
    bg,
    errorColor,
    linkColor,
    pageBg,
    textColor,
    lightGradient,
    darkGradient,
  } = useFormTheme();

  const validate = (): boolean => {
    if (!email) {
      setError(String(t("auth.common.emailRequired")));
      return false;
    } else if (!validateEmail(email)) {
      setError(String(t("auth.common.invalidEmail")));
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const lang = i18n.language?.toLowerCase().split("-")[0] || "fr";
      await sendResetCodeHandler(email, lang);
      toast({
        title: String(t("auth.forgotPassword.successTitle")),
        description: String(t("auth.forgotPassword.successDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      navigate("/reset-password", { state: { email }, replace: true });
    } catch (err) {
      const message = getLocalizedAuthErrorMessage(err, t);
      setError(message);
      toast({
        title: String(t("auth.forgotPassword.errorTitle")),
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center position="relative" minH="100vh" overflow="hidden">
      <Box
        position="fixed"
        top={0}
        left={0}
        zIndex={-1}
        h="100vh"
        w="100vw"
        bgGradient={lightGradient}
        _dark={{ bgGradient: darkGradient }}
        bg={pageBg}
      />
      <MotionBox
        maxW={{ base: "90%", md: "400px" }}
        px={{ base: "24px", md: "40px" }}
        w="full"
        bg={bg}
        boxShadow="md"
        borderRadius="md"
        py="44px"
        layout
        transition={authCardTransition}
      >
        <AuthLogo />

        <Center mb={6}>
          <Text fontSize="16px" fontWeight="medium" color={textColor}>
            {t("auth.forgotPassword.title")}
          </Text>
        </Center>

        <form onSubmit={handleSubmit} autoComplete="off">
          <VStack spacing={{ base: "24px", md: "32px" }} align="stretch">
            <FormControl isInvalid={!!error}>
              <FormLabel>{t("auth.login.email")}</FormLabel>
              <CustomInput
                type="email"
                error={!!error}
                placeholder={String(t("auth.forgotPassword.emailPlaceholder"))}
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (hasSubmitted) {
                    if (!value) {
                      setError(String(t("auth.common.emailRequired")));
                    } else if (!validateEmail(value)) {
                      setError(String(t("auth.common.invalidEmail")));
                    } else {
                      setError("");
                    }
                  }
                }}
              />
              <FormErrorMessage color={errorColor}>{error}</FormErrorMessage>
            </FormControl>

            <FormErrorBox errors={error ? [error] : []} />

            <CustomButton type="submit" isDisabled={loading || isLoading}>
              {loading || isLoading ? (
                <Spinner size="sm" />
              ) : (
                t("auth.forgotPassword.submit")
              )}
            </CustomButton>

            <Center>
              <Text
                fontSize="14px"
                color={linkColor}
                textDecor="underline"
                cursor="pointer"
                onClick={() => navigate("/login")}
              >
                {t("auth.verify.backToLogin")}
              </Text>
            </Center>
          </VStack>
        </form>
      </MotionBox>
    </Center>
  );
};

export default ForgotPasswordPage;
